import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api/api';
import './DocumentUpload.css';

const DocumentUpload = ({ merchantId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [documentType, setDocumentType] = useState('PAN_CARD');
  const [documentNumber, setDocumentNumber] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const documentTypes = [
    { value: 'PAN_CARD', label: 'PAN Card' },
    { value: 'AADHAAR_CARD', label: 'Aadhaar Card' },
    { value: 'GST_CERTIFICATE', label: 'GST Certificate' },
    { value: 'CANCELLED_CHEQUE', label: 'Cancelled Cheque' },
    { value: 'BANK_STATEMENT', label: 'Bank Statement' },
    { value: 'BUSINESS_REGISTRATION', label: 'Business Registration' },
    { value: 'INCORPORATION_CERTIFICATE', label: 'Incorporation Certificate' },
    { value: 'UTILITY_BILL', label: 'Utility Bill (Address Proof)' },
    { value: 'OTHER', label: 'Other' }
  ];

  useEffect(() => {
    console.log('DocumentUpload useEffect triggered, merchantId:', merchantId);
    if (merchantId) {
      fetchDocuments();
    } else {
      setLoading(false);
      setDocuments([]);
    }
  }, [merchantId]);

  const fetchDocuments = async () => {
    console.log('fetchDocuments called, merchantId:', merchantId);
    setLoading(true);
    try {
      // Use mock data for demo mode
      if (!merchantId) {
        console.log('No merchantId, setting empty documents');
        setDocuments([]);
        return;
      }

      console.log('Simulating API call...');
      // Simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const mockDocuments = [
        {
          id: 'doc_1',
          fileName: 'pan_card_demo.jpg',
          documentType: 'PAN_CARD',
          documentNumber: 'ABCDE1234F',
          uploadedAt: new Date().toISOString(),
          status: 'VERIFIED'
        },
        {
          id: 'doc_2',
          fileName: 'aadhaar_demo.jpg',
          documentType: 'AADHAAR_CARD',
          documentNumber: '1234-5678-9012',
          uploadedAt: new Date().toISOString(),
          status: 'PENDING'
        }
      ];
      
      console.log('Setting mock documents:', mockDocuments);
      setDocuments(mockDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents');
    } finally {
      console.log('fetchDocuments completed, setting loading to false');
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setError('');
    
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF, JPG, and PNG files are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('merchantId', merchantId);
      formData.append('documentType', documentType);
      formData.append('documentNumber', documentNumber);
      formData.append('file', selectedFile);

      // Simulate document upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create new document object
      const newDocument = {
        id: 'doc_' + Date.now(),
        fileName: selectedFile.name,
        documentType: documentType,
        documentNumber: documentNumber,
        uploadedAt: new Date().toISOString(),
        status: 'PENDING'
      };
      
      // Add to documents list immediately
      setDocuments(prev => [...prev, newDocument]);
      
      // Mock successful upload
      setSuccess('Document uploaded successfully!');
      setSelectedFile(null);
      setFilePreview(null);
      setDocumentNumber('');
      document.getElementById('fileInput').value = '';
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      // Simulate document deletion
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Remove document from list immediately
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Mock successful deletion
      setSuccess('Document deleted successfully');
    } catch (error) {
      setError('Failed to delete document');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { class: 'status-pending', text: 'Pending Review' },
      IN_REVIEW: { class: 'status-review', text: 'In Review' },
      VERIFIED: { class: 'status-verified', text: 'Verified' },
      REJECTED: { class: 'status-rejected', text: 'Rejected' }
    };
    return badges[status] || badges.PENDING;
  };

  return (
    <div className="document-upload-container">
      <h3>Upload Documents</h3>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="upload-section">
        <div className="form-group">
          <label>Document Type *</label>
          <select 
            value={documentType} 
            onChange={(e) => setDocumentType(e.target.value)}
            className="form-control"
          >
            {documentTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Document Number (Optional)</label>
          <input
            type="text"
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder="e.g., PAN: ABCDE1234F"
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>Select File * (PDF, JPG, PNG - Max 5MB)</label>
          <input
            id="fileInput"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            className="form-control"
          />
          {selectedFile && (
            <div className="file-preview-container">
              <div className="file-info">
                <div className="file-details">
                  <span className="file-name">📄 {selectedFile.name}</span>
                  <span className="file-size">({(selectedFile.size / 1024).toFixed(2)} KB)</span>
                </div>
                {filePreview && (
                  <div className="image-preview">
                    <img src={filePreview} alt="Uploaded document preview" className="preview-image" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <button 
          onClick={handleUpload} 
          disabled={uploading || !selectedFile}
          className="btn btn-primary"
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </button>
      </div>

      <div className="documents-list">
        <h4>Uploaded Documents</h4>
        {loading ? (
          <div className="loading-documents">
            <div className="loading-spinner"></div>
            <p>Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <p className="no-documents">No documents uploaded yet</p>
        ) : (
          <table className="documents-table" role="table">
            <caption className="sr-only">Uploaded KYC Documents</caption>
            <thead>
              <tr>
                <th scope="col">Document Type</th>
                <th scope="col">Document Number</th>
                <th scope="col">File Name</th>
                <th scope="col">Upload Date</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => {
                const badge = getStatusBadge(doc.status);
                return (
                  <tr key={doc.id}>
                    <td>{doc.documentType.replace(/_/g, ' ')}</td>
                    <td>{doc.documentNumber || '-'}</td>
                    <td>{doc.fileName}</td>
                    <td>{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${badge.class}`}>
                        {badge.text}
                      </span>
                    </td>
                    <td>
                      <button 
                        onClick={() => handleDelete(doc.id)}
                        className="btn btn-sm btn-danger"
                        disabled={doc.status === 'VERIFIED'}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DocumentUpload;
